<template lang="pug">
div
    feathers-vuex-find(service="vw-user-groups" :query="dataQuery" :fetch-query="fetchQuery" watch="query")
        v-card(slot-scope="{ items: groups, pagination: serverPagination, isFindPending }")
            v-card-title Group Campaign Settings
                v-spacer
                v-text-field(v-model="searchText" append-icon="mdi-magnify" label="Search by group name" single-line hide-details)
            
            v-data-table(
                :headers="headers"
                :loading="isFindPending"
                :items="isFindPending ? [] : groups"
                :options.sync="pagination"
                :server-items-length="!serverPagination ? 0 : serverPagination.mostRecent.total"
                loading-text="Loading groups..."
                :footer-props="{ showCurrentPage: true, itemsPerPageOptions: [10, 25, 50] }"
                item-key="group_id"
            )
                template(v-slot:item.group_name="{ item }")
                    v-avatar(size="32" style="margin-right: 10px;")
                        img(v-if="item.profile_picture_thumb || item.profile_picture" :src="item.profile_picture_thumb || item.profile_picture" @error="$event.target.style.display='none'")
                        v-icon(v-else color="grey") mdi-account-circle
                    span {{ item.group_name || (item.fname ? item.fname + ' ' + item.lname : 'Unnamed Group') }}

                template(v-slot:item.member_count="{ item }")
                    v-chip(small color="blue" text-color="white") {{ item.member_count || 0 }} members

                template(v-slot:item.admin_percentage="{ item }")
                    v-edit-dialog(
                        :return-value.sync="item.admin_percentage"
                        large
                        persistent
                        @save="savePercentage(item)"
                        @cancel="cancel"
                        @open="tempValue = seedPercentage(item)"
                    )
                        div(v-if="hasPercentage(item)")
                            | {{ item.admin_percentage }}% / {{ memberPercentageOf(item) }}%
                            v-icon(v-if="!percentagesReconcile(item)" small color="orange" title="Stored admin % and member % do not total 100%") mdi-alert
                        div.grey--text.font-italic(v-else) Not set
                        template(v-slot:input)
                            v-text-field(
                                v-model.number="tempValue"
                                label="Admin Percentage"
                                type="number"
                                min="0"
                                max="100"
                                single-line
                                counter
                                suffix="%"
                                :rules="[rules.required, rules.percentage]"
                            )
                            small.grey--text(v-if="isValidTempValue") Member percentage will be {{ 100 - tempValue }}%
                            small.red--text(v-else) Enter a whole number between 0 and 100

                template(v-slot:item.is_monetized="{ item }")
                    v-switch(
                        :input-value="item.is_monetized === 1"
                        @change="toggleMonetization(item, $event)"
                        :loading="monetizationLoading[item.group_id]"
                        :disabled="!!monetizationLoading[item.group_id]"
                        color="success"
                        hide-details
                    )

                template(v-slot:item.token_campaign_listed="{ item }")
                    v-switch(
                        :input-value="item.token_campaign_listed === 1"
                        @change="toggleCampaignListed(item, $event)"
                        :loading="campaignListedLoading[item.group_id]"
                        :disabled="!!campaignListedLoading[item.group_id]"
                        color="primary"
                        hide-details
                    )

                template(v-slot:item.tokens_per_rating="{ item }")
                    v-edit-dialog(
                        :return-value.sync="item.tokens_per_rating"
                        large
                        persistent
                        @save="saveTokensPerRating(item)"
                        @cancel="cancelTokensPerRating"
                        @open="tempTokensPerRating = item.tokens_per_rating || 10"
                    )
                        v-chip(small color="purple" text-color="white") {{ item.tokens_per_rating || 10 }} T/R
                        template(v-slot:input)
                            v-text-field(
                                v-model.number="tempTokensPerRating"
                                label="Tokens Per Rating"
                                type="number"
                                min="1"
                                single-line
                                counter
                                :rules="[rules.required, rules.minOne]"
                            )
                    
                template(v-slot:item.actions="{ item }")
                    v-btn(icon small color="blue" @click="openEditDialog(item)" title="Edit Percentages")
                        v-icon mdi-pencil

            v-dialog(v-model="editDialog" max-width="500px" persistent)
                v-card
                    v-card-title.headline Edit Group Settings
                    v-card-text
                        v-container
                            v-row
                                v-col(cols="12")
                                    strong Group: 
                                    | {{ editedItem.group_name }}
                            v-row
                                v-col(cols="12")
                                    v-text-field(
                                        v-model.number="editedItem.admin_percentage"
                                        label="Admin Percentage"
                                        type="number"
                                        min="0"
                                        max="100"
                                        suffix="%"
                                        :rules="[rules.required, rules.percentage]"
                                        outlined
                                    )
                            v-row
                                v-col(cols="12")
                                    v-text-field(
                                        :value="isValidPercentage ? 100 - editedItem.admin_percentage : ''"
                                        label="Member Percentage"
                                        suffix="%"
                                        outlined
                                        readonly
                                        disabled
                                    )
                            v-row
                                v-col(cols="12")
                                    v-text-field(
                                        v-model.number="editedItem.tokens_per_rating"
                                        label="Tokens Per Rating"
                                        type="number"
                                        min="1"
                                        :rules="[rules.required, rules.minOne]"
                                        outlined
                                        hint="Number of tokens given per rating for campaigns targeting this group"
                                        persistent-hint
                                    )
                            v-row
                                v-col(cols="12")
                                    v-alert(type="info" dense text)
                                        | Total must equal 100%. Member percentage automatically adjusts.
                    v-card-actions
                        v-spacer
                        v-btn(color="grey darken-1" text @click="closeEditDialog") Cancel
                        v-btn(color="blue darken-1" text @click="saveEditDialog" :disabled="!isValidPercentage") Save

            v-snackbar(top v-model="successMessage" color="success") {{ successText }}
            v-snackbar(top v-model="error.show" color="red") {{ error.message }}
</template>

<script>
// Per BHV3-88, the "80/20 default" means 80% to contributors / 20% to the group
// admin, so the admin's share is 20. Only used to seed the editor when a group has
// no stored value — never to display a value the group doesn't actually have.
const DEFAULT_ADMIN_PERCENTAGE = 20;

// A percentage is only usable if it is a whole number in [0, 100].
// Note the guards: '' >= 0 and null >= 0 are both true in JS, and parseInt('')
// is NaN, so a loose range check would happily persist NaN to the database.
const isPercentageInRange = value => {
    if (typeof value !== 'number' && typeof value !== 'string') return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    const parsed = Number(value);
    return !isNaN(parsed) && isFinite(parsed) && parsed % 1 === 0 && parsed >= 0 && parsed <= 100;
};

export default {
    name: 'GroupSettings',
    data() {
        return {
            searchText: '',
            tempValue: DEFAULT_ADMIN_PERCENTAGE,
            tempTokensPerRating: 10,
            editDialog: false,
            monetizationLoading: {},
            campaignListedLoading: {},
            editedItem: {
                group_id: null,
                group_name: '',
                admin_percentage: DEFAULT_ADMIN_PERCENTAGE,
                member_percentage: 100 - DEFAULT_ADMIN_PERCENTAGE,
                tokens_per_rating: 10
            },
            successMessage: false,
            successText: '',
            error: {
                show: false,
                message: ''
            },
            rules: {
                required: value => !!value || value === 0 || 'Required.',
                percentage: value => isPercentageInRange(value) || 'Must be a whole number between 0 and 100',
                minOne: value => (value >= 1) || 'Must be at least 1'
            },
            headers: [
                {
                    text: 'Group ID',
                    align: 'start',
                    sortable: true,
                    value: 'group_id',
                    width: 100
                },
                {
                    text: 'Group Name',
                    align: 'start',
                    sortable: true,
                    value: 'group_name',
                    width: 300
                },
                {
                    text: 'Members',
                    align: 'center',
                    sortable: true,
                    value: 'member_count',
                    width: 120
                },
                {
                    text: 'Admin % / Member %',
                    align: 'center',
                    sortable: true,
                    value: 'admin_percentage',
                    width: 180
                },
                {
                    text: 'Monetization',
                    align: 'center',
                    sortable: true,
                    value: 'is_monetized',
                    width: 140
                },
                {
                    text: 'Campaign Listed',
                    align: 'center',
                    sortable: true,
                    value: 'token_campaign_listed',
                    width: 150
                },
                {
                    text: 'Tokens/Rating',
                    align: 'center',
                    sortable: true,
                    value: 'tokens_per_rating',
                    width: 140
                },
                {
                    text: 'Actions',
                    align: 'end',
                    sortable: false,
                    value: 'actions',
                    width: 100
                }
            ],
            pagination: {
                page: 1,
                itemsPerPage: 25,
                sortBy: ['group_name'],
                sortDesc: [false]
            }
        };
    },
    computed: {
        dataQuery() {
            const query = {
                $limit: this.pagination.itemsPerPage,
                $skip: (this.pagination.page - 1) * this.pagination.itemsPerPage
            };

            if (this.searchText) {
                query.group_name = {
                    $like: `%${this.searchText}%`
                };
            }

            if (this.pagination.sortBy && this.pagination.sortBy.length > 0) {
                query.$sort = {};
                this.pagination.sortBy.forEach((field, index) => {
                    query.$sort[field] = this.pagination.sortDesc[index] ? -1 : 1;
                });
            }

            return query;
        },
        fetchQuery() {
            return Object.assign({}, this.dataQuery);
        },
        isValidPercentage() {
            return isPercentageInRange(this.editedItem.admin_percentage);
        },
        isValidTempValue() {
            return isPercentageInRange(this.tempValue);
        }
    },
    methods: {
        // Display helpers: show what is actually stored, never a guessed default.
        // A group legitimately set to 0% admin must render as 0%, not as the default.
        hasPercentage(item) {
            return item.admin_percentage !== null && item.admin_percentage !== undefined;
        },
        memberPercentageOf(item) {
            return isPercentageInRange(item.member_percentage)
                ? item.member_percentage
                : 100 - item.admin_percentage;
        },
        percentagesReconcile(item) {
            if (!isPercentageInRange(item.member_percentage)) return true;
            return item.admin_percentage + item.member_percentage === 100;
        },
        // Seeds the inline editor. Falls back to the default only when the group
        // has no stored value at all, so a stored 0 is preserved.
        seedPercentage(item) {
            return this.hasPercentage(item) ? item.admin_percentage : DEFAULT_ADMIN_PERCENTAGE;
        },
        async refreshData() {
            await this.$store.dispatch('vw-user-groups/find', {
                query: this.fetchQuery
            });
        },
        async savePercentage(item) {
            try {
                if (!isPercentageInRange(this.tempValue)) {
                    throw new Error('Percentage must be a whole number between 0 and 100');
                }

                const adminPercentage = Number(this.tempValue);
                const memberPercentage = 100 - adminPercentage;

                await this.$store.dispatch('bh-user-groups/patch', [
                    item.group_id,
                    { 
                        admin_percentage: adminPercentage,
                        member_percentage: memberPercentage
                    }
                ]);

                this.successText = `Updated percentages: ${adminPercentage}% admin / ${memberPercentage}% members`;
                this.successMessage = true;
                
                // Refresh the table data
                await this.refreshData();
            } catch (error) {
                this.error.message = `Error updating percentage: ${error.message}`;
                this.error.show = true;
            }
        },
        cancel() {
            this.tempValue = DEFAULT_ADMIN_PERCENTAGE;
        },
        cancelTokensPerRating() {
            this.tempTokensPerRating = 10;
        },
        async saveTokensPerRating(item) {
            try {
                const tokensPerRating = parseInt(this.tempTokensPerRating);
                if (tokensPerRating < 1) {
                    throw new Error('Tokens per rating must be at least 1');
                }

                await this.$store.dispatch('bh-user-groups/patch', [
                    item.group_id,
                    { 
                        tokens_per_rating: tokensPerRating
                    }
                ]);

                this.successText = `Updated tokens per rating to ${tokensPerRating} for "${item.group_name}"`;
                this.successMessage = true;
                
                await this.refreshData();
            } catch (error) {
                this.error.message = `Error updating tokens per rating: ${error.message}`;
                this.error.show = true;
            }
        },
        openEditDialog(item) {
            const adminPercentage = this.seedPercentage(item);
            this.editedItem = {
                group_id: item.group_id,
                group_name: item.group_name || (item.fname ? item.fname + ' ' + item.lname : 'Unnamed Group'),
                admin_percentage: adminPercentage,
                member_percentage: 100 - adminPercentage,
                tokens_per_rating: item.tokens_per_rating || 10
            };
            this.editDialog = true;
        },
        closeEditDialog() {
            this.editDialog = false;
            this.editedItem = {
                group_id: null,
                group_name: '',
                admin_percentage: DEFAULT_ADMIN_PERCENTAGE,
                member_percentage: 100 - DEFAULT_ADMIN_PERCENTAGE,
                tokens_per_rating: 10
            };
        },
        async saveEditDialog() {
            try {
                if (!isPercentageInRange(this.editedItem.admin_percentage)) {
                    throw new Error('Percentage must be a whole number between 0 and 100');
                }

                const adminPercentage = Number(this.editedItem.admin_percentage);
                const memberPercentage = 100 - adminPercentage;
                const tokensPerRating = parseInt(this.editedItem.tokens_per_rating) || 10;

                await this.$store.dispatch('bh-user-groups/patch', [
                    this.editedItem.group_id,
                    { 
                        admin_percentage: adminPercentage,
                        member_percentage: memberPercentage,
                        tokens_per_rating: tokensPerRating
                    }
                ]);

                this.successText = `Updated ${this.editedItem.group_name}: ${adminPercentage}% admin / ${memberPercentage}% members`;
                this.successMessage = true;
                this.closeEditDialog();
                
                // Refresh the table data
                await this.refreshData();
            } catch (error) {
                this.error.message = `Error updating percentage: ${error.message}`;
                this.error.show = true;
            }
        },
        async toggleMonetization(item, enabled) {
            this.$set(this.monetizationLoading, item.group_id, true);
            
            try {
                await this.$store.dispatch('bh-user-groups/patch', [
                    item.group_id,
                    { 
                        is_monetized: enabled ? 1 : 0
                    }
                ]);

                // Immediately update the vw-user-groups store item to stay in sync
                if (item.group_id != null) {
                    const { VwUserGroups } = this.$FeathersVuex.api;
                    const existing = VwUserGroups.getFromStore(item.group_id);
                    if (existing) {
                        existing.is_monetized = enabled ? 1 : 0;
                        existing.commit();
                    }
                }

                this.successText = `Group "${item.group_name}" monetization ${enabled ? 'enabled' : 'disabled'}`;
                this.successMessage = true;
                
                // Refresh the table data from server
                await this.refreshData();
            } catch (error) {
                this.error.message = `Error toggling monetization: ${error.message}`;
                this.error.show = true;
                
                // Refresh to revert visual state on error
                await this.refreshData();
            } finally {
                this.$set(this.monetizationLoading, item.group_id, false);
            }
        },
        async toggleCampaignListed(item, enabled) {
            this.$set(this.campaignListedLoading, item.group_id, true);
            
            try {
                await this.$store.dispatch('bh-user-groups/patch', [
                    item.group_id,
                    { 
                        token_campaign_listed: enabled ? 1 : 0
                    }
                ]);

                // Immediately update the vw-user-groups store item to stay in sync
                if (item.group_id != null) {
                    const { VwUserGroups } = this.$FeathersVuex.api;
                    const existing = VwUserGroups.getFromStore(item.group_id);
                    if (existing) {
                        existing.token_campaign_listed = enabled ? 1 : 0;
                        existing.commit();
                    }
                }

                this.successText = `Group "${item.group_name}" campaign listing ${enabled ? 'enabled' : 'disabled'}`;
                this.successMessage = true;
                
                // Refresh the table data from server
                await this.refreshData();
            } catch (error) {
                this.error.message = `Error toggling campaign listing: ${error.message}`;
                this.error.show = true;
                
                // Refresh to revert visual state on error
                await this.refreshData();
            } finally {
                this.$set(this.campaignListedLoading, item.group_id, false);
            }
        }
    }
};
</script>

<style scoped>
.v-edit-dialog {
    cursor: pointer;
}
</style>
